import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const InvoiceHeader = ({
  invoiceNumber,
  onBack,
  onApprove,
  onReject,
  onHistory,
  onAssignTo,
  onStop,
  onStart,
  approverRoles = [],
  loadingRoles = false,
  assigningRole = false,
  loadingAction = null, // 'approve', 'reject', 'stop', 'start', or null
  invoiceStatus = '', // Invoice status to determine if we show Start or Stop button
  assignedRoleName = '', // Assigned role name to determine if we show Invoice Tracking / Stop
  showStopStart = false, // When true (e.g. Planning L4/L5), show Stop/Start buttons like admin
}) => {
  const [forwardMenuAnchor, setForwardMenuAnchor] = useState(null);
  const [buttonWidth, setButtonWidth] = useState(null);

  const isActionLoading = loadingAction !== null;
  const isStopped = (invoiceStatus || '').toLowerCase() === 'stop';
  const statusLower = (invoiceStatus || '').toLowerCase();
  const isPending = statusLower === 'pending' || statusLower === 'pending_rejection';

  // Only show action buttons if status is 'pending' or 'stop'
  const showActionButtons = isPending || isStopped;

  // Hide Invoice Tracking for Ro L1, Ro L2, Ro L3 roles
  const roleNameLower = (assignedRoleName || '').toLowerCase().trim();
  const hideInvoiceTracking = ['ro l1', 'ro l2', 'ro l3'].includes(roleNameLower);

  const showForwardTo = Boolean(onAssignTo && isPending);

  // Show Stop/Start when showStopStart (Planning L4 or Planning L5) and we have handlers
  const showStopStartButtons = showStopStart && (onStop || onStart);

  const handleForwardClick = (event) => {
    const buttonElement = event.currentTarget;
    setButtonWidth(buttonElement.offsetWidth);
    setForwardMenuAnchor(buttonElement);
  };

  const handleForwardClose = () => {
    setForwardMenuAnchor(null);
  };

  const handleAssignToRole = (role) => {
    if (assigningRole) {
      return;
    }
    if (onAssignTo) {
      onAssignTo(role);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 3,
        pb: 2,
        borderBottom: '1px solid #E8E9E9',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton
          onClick={onBack}
          disableRipple
          sx={{
            backgroundColor: '#FFF',
            width: 40,
            height: 40,
            borderRadius: '8px',
            boxShadow: '0 0 30px 2px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <ArrowBackIosIcon sx={{ ml: '2px', fontSize: '16px' }} />
        </IconButton>
        <Typography
          sx={{
            fontSize: '24px',
            fontFamily: 'Inter',
            fontWeight: 700,
            color: 'black',
          }}
        >
          Invoice {invoiceNumber}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {!hideInvoiceTracking && (
          <Button
            variant="outlined"
            onClick={onHistory}
            sx={{
              borderColor: '#E8E9E9',
              color: '#192126',
              textTransform: 'none',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: 500,
              '&:hover': {
                borderColor: '#BD1D23',
                backgroundColor: 'rgba(189, 29, 35, 0.04)',
              },
            }}
          >
            Invoice Tracking
          </Button>
        )}

        {/* Stop/Start - for Planning L4 and Planning L5 only (same as admin) */}
        {showStopStartButtons &&
          (isStopped && onStart ? (
            <Button
              variant="outlined"
              onClick={onStart}
              disabled={isActionLoading || assigningRole}
              startIcon={
                loadingAction === 'start' ? <CircularProgress size={16} color="inherit" /> : null
              }
              sx={{
                borderColor: '#10B981',
                color: '#10B981',
                textTransform: 'none',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 500,
                '&:hover': {
                  borderColor: '#10B981',
                  backgroundColor: 'rgba(16, 185, 129, 0.04)',
                },
                '&:disabled': { borderColor: '#E8E9E9', color: '#9CA3AF' },
              }}
            >
              Start
            </Button>
          ) : isPending && onStop ? (
            <Button
              variant="outlined"
              onClick={onStop}
              disabled={isActionLoading || assigningRole}
              startIcon={
                loadingAction === 'stop' ? <CircularProgress size={16} color="inherit" /> : null
              }
              sx={{
                borderColor: '#BD1D23',
                color: '#BD1D23',
                textTransform: 'none',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 500,
                '&:hover': {
                  borderColor: '#BD1D23',
                  backgroundColor: 'rgba(189, 29, 35, 0.04)',
                },
                '&:disabled': { borderColor: '#E8E9E9', color: '#9CA3AF' },
              }}
            >
              Stop
            </Button>
          ) : null)}

        {showForwardTo && (
          <>
            <Button
              variant="outlined"
              endIcon={<ArrowDropDownIcon />}
              onClick={handleForwardClick}
              disabled={assigningRole}
              sx={{
                borderColor: '#E8E9E9',
                color: '#192126',
                textTransform: 'none',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 500,
                '&:hover': {
                  borderColor: '#BD1D23',
                  backgroundColor: 'rgba(189, 29, 35, 0.04)',
                },
              }}
            >
              Forward to
            </Button>
            <Menu
              anchorEl={forwardMenuAnchor}
              open={Boolean(forwardMenuAnchor)}
              onClose={handleForwardClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  minWidth: buttonWidth ? `${buttonWidth}px` : '200px',
                  mt: 0.5,
                  maxHeight: '400px',
                  '& .MuiMenuItem-root': {
                    minHeight: '36px',
                    padding: '8px 16px',
                    '&:not(:last-child)': { borderBottom: '1px solid #E8E9E9' },
                  },
                },
              }}
            >
              {loadingRoles ? (
                <MenuItem disabled>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Loading roles...
                    </Typography>
                  </Box>
                </MenuItem>
              ) : approverRoles.length === 0 ? (
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary">
                    No roles available
                  </Typography>
                </MenuItem>
              ) : assigningRole ? (
                <MenuItem disabled>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Forwarding invoice...
                    </Typography>
                  </Box>
                </MenuItem>
              ) : (
                approverRoles.map((role) => (
                  <MenuItem key={role.id} onClick={() => handleAssignToRole(role)}>
                    <Typography variant="body2" sx={{ fontWeight: 400, color: '#192126' }}>
                      {role.invoice_type ? `${role.name} (${role.invoice_type})` : role.name}
                    </Typography>
                  </MenuItem>
                ))
              )}
            </Menu>
          </>
        )}

        {showActionButtons && (
          <>
            {/* Start/Stop functionality moved to Admin Invoice Management */}
            {/* {isStopped ? (
              <Button
                variant="contained"
                onClick={onStart}
                disabled={isActionLoading}
                startIcon={
                  loadingAction === 'start' ? <CircularProgress size={16} color="inherit" /> : null
                }
                sx={{
                  backgroundColor: '#10B981', // Green color for Start
                  color: '#FFFFFF',
                  textTransform: 'none',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: '#0B875B',
                  },
                  '&:disabled': {
                    backgroundColor: '#D1D5DB',
                    color: '#FFFFFF',
                  },
                }}
              >
                Start
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={onStop}
                  disabled={isActionLoading}
                  startIcon={
                    loadingAction === 'stop' ? <CircularProgress size={16} color="inherit" /> : null
                  }
                  sx={{
                    borderColor: '#BD1D23',
                    color: '#BD1D23', // Red color for Stop
                    textTransform: 'none',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: 500,
                    '&:hover': {
                      borderColor: '#BD1D23',
                      backgroundColor: 'rgba(189, 29, 35, 0.04)',
                    },
                    '&:disabled': {
                      borderColor: '#E8E9E9',
                      color: '#9CA3AF',
                    },
                  }}
                >
                  Stop
                </Button>
            )} */}
            {!isStopped && (onReject || onApprove) && (
              <>
                {/* <Button
                  variant="outlined"
                  onClick={onRevert}
                  disabled={isActionLoading}
                  startIcon={
                    loadingAction === 'revert' ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                  sx={{
                    borderColor: 'black',
                    color: '#192126',
                    textTransform: 'none',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: 500,
                    '&:hover': {
                      borderColor: '#BD1D23',
                      backgroundColor: 'rgba(189, 29, 35, 0.04)',
                    },
                    '&:disabled': {
                      borderColor: '#E8E9E9',
                      color: '#9CA3AF',
                    },
                  }}
                >
                  Revert
                </Button> */}

                {onReject && (
                  <Button
                    variant="outlined"
                    onClick={onReject}
                    disabled={isActionLoading}
                    startIcon={
                      loadingAction === 'reject' ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : null
                    }
                    sx={{
                      backgroundColor: '#d32f2f',
                      color: '#FFFFFF',
                      textTransform: 'none',
                      padding: '8px 24px',
                      fontSize: '14px',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: '#d32f2f',
                      },
                      '&:disabled': {
                        backgroundColor: '#D1D5DB',
                        color: '#FFFFFF',
                      },
                    }}
                  >
                    Reject
                  </Button>
                )}

                {onApprove && (
                  <Button
                    variant="contained"
                    onClick={onApprove}
                    disabled={isActionLoading}
                    startIcon={
                      loadingAction === 'approve' ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : null
                    }
                    sx={{
                      backgroundColor: '#2e7d32',
                      textTransform: 'none',
                      padding: '8px 24px',
                      fontSize: '14px',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: '#1b5e20',
                      },
                      '&:disabled': {
                        backgroundColor: '#D1D5DB',
                        color: '#FFFFFF',
                      },
                    }}
                  >
                    Approve
                  </Button>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default InvoiceHeader;
